// src/sectors/pilates/sms/components/tabs/SmsTabLastMessages.jsx
import React from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { SoftCard } from '../../ui/SoftCard';
import { smsApi } from '../../api/smsApi';

/* ---------------- helpers ---------------- */
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR');
}

function typeLabel(t) {
  switch (t) {
    case 'ATTENDANCE_REMINDER':
      return 'Yoklama Hatırlatma';
    case 'PAYMENT_REMINDER':
      return 'Ödeme Hatırlatma';
    case 'PAID_SESSION_INFO':
      return 'Ücretli Seans';
    case 'BIRTHDAY':
      return 'Doğum Günü';
    case 'MANUAL':
      return 'Manuel';
    default:
      return t || '—';
  }
}

function statusColor(s) {
  switch (s) {
    case 'SENT':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'QUEUED':
    default:
      return 'warning';
  }
}

/* ---------------- component ---------------- */
export function SmsTabLastMessages({ sms }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const canUse = !sms?.gate?.closed && !sms?.gate?.noQuota;

  React.useEffect(() => {
    if (!canUse) return;

    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await smsApi.getLogs();
        if (!alive) return;
        setRows(Array.isArray(res) ? res : []);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Son mesajlar alınamadı.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [canUse]);

  if (!canUse) {
    return (
      <Alert severity="info">
        Modül aktif değil veya mesaj hakkı yok. Son mesajlar gösterilemiyor.
      </Alert>
    );
  }

  if (loading) {
    return (
      <MDBox display="flex" alignItems="center" gap={1}>
        <CircularProgress size={18} />
        <MDTypography variant="button">Yükleniyor…</MDTypography>
      </MDBox>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!rows.length) {
    return (
      <SoftCard>
        <MDTypography variant="button" sx={{ opacity: 0.75 }}>
          Henüz gönderilmiş mesaj yok.
        </MDTypography>
      </SoftCard>
    );
  }

  return (
    <MDBox display="grid" gap={1}>
      {rows.map((r) => (
        <SoftCard key={r.id}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography fontWeight="bold" fontSize={13}>
              {typeLabel(r.smsType)}
            </MDTypography>

            <MDBox display="flex" gap={0.75}>
              {r.test ? <Chip size="small" label="TEST" variant="outlined" /> : null}
              <Chip
                size="small"
                label={r.status}
                color={statusColor(r.status)}
                variant="outlined"
              />
            </MDBox>
          </MDBox>

          <Divider sx={{ my: 0.75 }} />

          <MDTypography variant="button" sx={{ opacity: 0.85 }}>
            {r.content}
          </MDTypography>

          <MDBox mt={0.75} display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="caption" sx={{ opacity: 0.65 }}>
              {fmtDate(r.createdAt)}
            </MDTypography>

            <MDTypography variant="caption" sx={{ opacity: 0.65 }}>
              {r.toPhone}
            </MDTypography>
          </MDBox>
        </SoftCard>
      ))}
    </MDBox>
  );
}
